/**
 * Browser tab titles ("Courses · Learnbee") and "go back to wherever you came
 * from" (agreed 30 Sep 2026).
 *
 * Pages people return TO — Home, Courses, Templates, Library, a collection, a
 * course's page, Members… — call `usePageTitle(name, { returnPoint: true })`,
 * which records that page (path + its on-screen name) as the place to go back
 * to. The pages people step INTO — the wizard, the editor, the preview,
 * Translate — call `useReturnPoint()` and label their back button with that
 * name ("Back to Courses"), so the label always matches where it goes.
 *
 * Kept in sessionStorage: per tab, survives a refresh, and a link opened in a
 * new tab starts with no return point and falls back to Home.
 */

import { useEffect, useState } from 'react'

const KEY = 'learnbee_return_point'

export interface ReturnPoint { path: string; name: string }

export const HOME: ReturnPoint = { path: '/', name: 'Home' }

export function usePageTitle(name: string | null | undefined, opts: { returnPoint?: boolean } = {}): void {
  const returnPoint = !!opts.returnPoint
  useEffect(() => {
    if (!name) return
    document.title = `${name} · Learnbee`
    if (returnPoint) {
      try {
        sessionStorage.setItem(KEY, JSON.stringify({ path: window.location.pathname + window.location.search, name }))
      } catch { /* private mode — back buttons fall back */ }
    }
  }, [name, returnPoint])
}

/** Where "back" goes from a page people step into, and what to call it. */
export function useReturnPoint(fallback: ReturnPoint = HOME): ReturnPoint {
  const [point] = useState<ReturnPoint>(() => {
    try {
      const v = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as ReturnPoint | null
      return v && typeof v.path === 'string' && v.path.startsWith('/') && typeof v.name === 'string' ? v : fallback
    } catch { return fallback }
  })
  return point
}
